/**
 * `createAuthClient` - framework-free auth client: single-flight refresh,
 * 401-aware `fetch`, logout that always clears locally, and cross-tab sync.
 *
 * Runs in browsers, workers and React Native / Hermes: no React, and no DOM or
 * platform global is touched at import time (`BroadcastChannel`, `Request`,
 * `ReadableStream` are all feature-detected at call time).
 *
 * The refresh path is modelled on `createMobileAuth.refreshAccessToken`
 * (`src/mobile.ts`) and keeps its failure policy: only a DEFINITIVE rejection
 * (401/403, or `success: false`) clears credentials. A 429 (per-IP refresh
 * limiter), a 5xx or a network error is TRANSIENT - credentials are kept and a
 * later call retries (2026-10-04 mobile auth incident).
 *
 * Secrets: nothing in here logs, and the cross-tab channel only ever carries
 * `{ type, at }` - never a token or user data.
 */

import type { AuthUser } from "../types";
import { DEFAULT_BROADCAST_CHANNEL, openBroadcastChannel, parseBroadcastMessage } from "./broadcast";
import { createCookieTokenStore } from "./stores";
import type {
  AuthBroadcastMessage,
  AuthChangeEvent,
  AuthClient,
  AuthClientConfig,
  AuthSubscriber,
  AuthTokens,
  BroadcastChannelLike,
  LockManagerLike,
  RefreshOutcome,
} from "./types";

export const DEFAULT_REFRESH_LOCK = "plerity-auth-refresh";

/** Feature-detect the Web Locks API at call time (absent on RN / Hermes and old browsers). */
function detectLocks(): LockManagerLike | null {
  try {
    const nav = (globalThis as { navigator?: { locks?: LockManagerLike } }).navigator;
    return nav?.locks && typeof nav.locks.request === "function" ? nav.locks : null;
  } catch {
    return null;
  }
}

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (typeof URL !== "undefined" && input instanceof URL) return input.href;
  return (input as Request).url;
}

/**
 * Typed against `RequestInfo | URL` itself (not the global `Request` class) so the
 * narrowed type stays valid when `@cloudflare/workers-types` replaces the DOM
 * `Request` (the portal typechecks this package under that lib).
 */
function isRequest(input: RequestInfo | URL): input is Exclude<RequestInfo | URL, string | URL> {
  return typeof Request !== "undefined" && input instanceof Request;
}

/** A stream body is consumed by the first attempt, so the request cannot be re-sent. */
function isReplayable(init: RequestInit | undefined): boolean {
  const body = init?.body;
  return !(body && typeof ReadableStream !== "undefined" && body instanceof ReadableStream);
}

async function readJson(res: Response): Promise<Record<string, unknown> | null> {
  try {
    const parsed: unknown = await res.json();
    return typeof parsed === "object" && parsed !== null ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** Pull `{ success, data: { accessToken, refreshToken } }` (the portal mobile envelope) out of a body. */
function extractTokens(body: Record<string, unknown> | null): AuthTokens | null {
  const data = body?.data;
  if (typeof data !== "object" || data === null) return null;
  const { accessToken, refreshToken } = data as { accessToken?: unknown; refreshToken?: unknown };
  if (typeof accessToken !== "string" || accessToken === "") return null;
  return { accessToken, refreshToken: typeof refreshToken === "string" ? refreshToken : undefined };
}

export function createAuthClient(config: AuthClientConfig): AuthClient {
  const store = config.store ?? createCookieTokenStore();
  // ssrf-ok: browser-side client; URL is config.* endpoint URLs from the app's AuthClientConfig (own API base)
  const doFetch: typeof fetch = config.fetchImpl ?? ((input, init) => globalThis.fetch(input, init));
  const now = config.now ?? Date.now;
  const resolve = (url: string | (() => string)): string => (typeof url === "function" ? url() : url);

  let currentUser: AuthUser | null = config.initialUser ?? null;
  const subscribers = new Set<AuthSubscriber>();
  let inflight: Promise<RefreshOutcome> | null = null;
  /** Bumped by every sign-out so a refresh that was in flight cannot resurrect the session. */
  let epoch = 0;
  /** Bumped by every successful refresh (here or in another tab) so a late 401 skips a redundant refresh. */
  let refreshGeneration = 0;
  let disposed = false;

  // ── cross-tab channel ──────────────────────────────────────────────────────
  let channel: BroadcastChannelLike | null = null;
  if (config.broadcast !== false) {
    try {
      channel = (config.createBroadcastChannel ?? openBroadcastChannel)(
        config.broadcastChannelName ?? DEFAULT_BROADCAST_CHANNEL
      );
    } catch {
      channel = null;
    }
  }

  function broadcast(type: AuthBroadcastMessage["type"]): void {
    if (!channel || disposed) return;
    const message: AuthBroadcastMessage = { type, at: now() };
    try {
      channel.postMessage(message);
    } catch {
      // A closed/unsupported channel must never break an auth flow.
    }
  }

  // ── state ──────────────────────────────────────────────────────────────────
  function emit(next: AuthUser | null, event: AuthChangeEvent): void {
    if (next === currentUser) return;
    currentUser = next;
    for (const cb of [...subscribers]) {
      try {
        cb(next, event);
      } catch {
        // One bad subscriber must not starve the others.
      }
    }
  }

  async function safeClear(): Promise<void> {
    try {
      await store.clear();
    } catch {
      // Clearing is best-effort; the in-memory user is dropped regardless.
    }
  }

  async function safeResolveUser(source: "refresh" | "broadcast"): Promise<AuthUser | null | undefined> {
    if (!config.resolveUser) return undefined;
    try {
      return await config.resolveUser({ token: await store.read(), source });
    } catch {
      return undefined;
    }
  }

  async function clearSession(): Promise<void> {
    epoch++;
    await safeClear();
    emit(null, "signed-out");
    broadcast("signed-out");
  }

  if (channel) {
    channel.onmessage = (event) => {
      const message = parseBroadcastMessage(event.data);
      if (!message) return;
      void handleRemote(message);
    };
  }

  async function handleRemote(message: AuthBroadcastMessage): Promise<void> {
    if (disposed) return;
    if (message.type === "signed-out") {
      epoch++;
      await safeClear();
      emit(null, "remote-signed-out");
      return;
    }
    refreshGeneration++;
    const user = await safeResolveUser("broadcast");
    if (user && !disposed) emit(user, "remote-refreshed");
  }

  // ── refresh ────────────────────────────────────────────────────────────────
  async function runRefresh(): Promise<RefreshOutcome> {
    const startEpoch = epoch;
    try {
      const headers: Record<string, string> = {};
      if (store.readRefreshToken) {
        const refreshToken = await store.readRefreshToken();
        // Token mode with no refresh token: nothing to refresh with, no network.
        if (!refreshToken) return { status: "unavailable" };
        headers.Authorization = `Bearer ${refreshToken}`;
      }

      const res = await doFetch(resolve(config.refreshUrl), {
        method: "POST",
        credentials: store.credentials,
        headers,
      });

      // Signed out while this request was in flight: drop the result.
      if (startEpoch !== epoch) return { status: "rejected", httpStatus: res.status };

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) return await reject(res.status);
        return { status: "unavailable", httpStatus: res.status };
      }

      const body = await readJson(res);
      if (body?.success === false) return await reject(res.status);

      const tokens = extractTokens(body);
      if (tokens) await store.write(tokens);
      // Token mode cannot proceed without tokens (e.g. a non-JSON edge page returned 200).
      else if (store.readRefreshToken) return { status: "unavailable", httpStatus: res.status };

      refreshGeneration++;
      const resolved = await safeResolveUser("refresh");
      if (startEpoch !== epoch) return { status: "rejected" };
      const user = resolved === undefined ? currentUser : resolved;
      emit(user, "refreshed");
      broadcast("refreshed");
      return { status: "refreshed", user, userResolved: resolved !== undefined };
    } catch {
      // Network error or a throwing store: transient, credentials kept.
      return { status: "unavailable" };
    }
  }

  async function reject(httpStatus: number): Promise<RefreshOutcome> {
    await safeClear();
    if (!config.keepUserOnRejected) emit(null, "rejected");
    return { status: "rejected", httpStatus };
  }

  // ── cross-tab lock ─────────────────────────────────────────────────────────
  const locks: LockManagerLike | null =
    config.crossTabLock === false ? null : config.locks !== undefined ? config.locks : detectLocks();

  function runRefreshLocked(): Promise<RefreshOutcome> {
    if (!locks) return runRefresh();
    const generationAtRequest = refreshGeneration;
    try {
      return locks
        .request(config.lockName ?? DEFAULT_REFRESH_LOCK, async () => {
          // Another tab refreshed while we waited for the lock (its "refreshed"
          // broadcast already bumped the generation): the shared credentials are
          // fresh, so don't spend the refresh token a second time.
          if (refreshGeneration !== generationAtRequest) {
            return { status: "refreshed", user: currentUser, userResolved: false } as RefreshOutcome;
          }
          return runRefresh();
        })
        .catch(() => runRefresh());
    } catch {
      // A throwing lock manager must never block refresh.
      return runRefresh();
    }
  }

  function refresh(): Promise<RefreshOutcome> {
    if (inflight) return inflight;
    const promise = runRefreshLocked().finally(() => {
      if (inflight === promise) inflight = null;
    });
    inflight = promise;
    return promise;
  }

  // ── fetch ──────────────────────────────────────────────────────────────────
  async function send(input: RequestInfo | URL, init: RequestInit | undefined): Promise<Response> {
    const token = await store.read();
    const headers = new Headers(isRequest(input) ? input.headers : undefined);
    new Headers(init?.headers).forEach((value, key) => {
      headers.set(key, value);
    });
    if (token && !headers.has("Authorization")) headers.set("Authorization", `Bearer ${token}`);
    return doFetch(input, { ...init, headers, credentials: init?.credentials ?? store.credentials });
  }

  async function authedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    if (config.shouldAttachAuth && !config.shouldAttachAuth(urlOf(input))) return doFetch(input, init);

    // A Request body is single-use; keep a clone so the retry can re-send it.
    // (The cast keeps this typechecking under @cloudflare/workers-types, whose
    // generic Request<CfHostMetadata, Cf> clone() is not assignable to RequestInfo.)
    const replay: RequestInfo | URL = isRequest(input) ? (input.clone() as unknown as RequestInfo) : input;
    const generationAtSend = refreshGeneration;

    const first = await send(input, init);
    if (first.status !== 401 || !isReplayable(init)) return first;

    // If a refresh finished after this request was sent, its 401 is just a stale
    // token - retry with the new one instead of refreshing again.
    if (refreshGeneration === generationAtSend) {
      const outcome = await refresh();
      if (outcome.status === "rejected") {
        config.onUnauthenticated?.();
        return first;
      }
      if (outcome.status === "unavailable") return first;
    }

    void first.body?.cancel().catch(() => undefined);
    const retry = await send(replay, init);
    if (retry.status === 401) config.onUnauthenticated?.();
    return retry;
  }

  // ── logout ─────────────────────────────────────────────────────────────────
  async function logout(): Promise<{ revoked: boolean }> {
    let revoked = false;
    try {
      const token = await store.read();
      const headers: Record<string, string> = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await doFetch(resolve(config.logoutUrl), {
        method: "POST",
        credentials: store.credentials,
        headers,
      });
      revoked = res.ok;
    } catch {
      // Offline / server down: still sign out locally below.
    }
    await clearSession();
    return { revoked };
  }

  return {
    user: () => currentUser,
    setUser: (user) => emit(user, "set"),
    subscribe: (cb) => {
      subscribers.add(cb);
      return () => {
        subscribers.delete(cb);
      };
    },
    refresh,
    fetch: authedFetch,
    logout,
    clearSession,
    dispose: () => {
      disposed = true;
      subscribers.clear();
      if (channel) {
        channel.onmessage = null;
        try {
          channel.close();
        } catch {
          // already closed
        }
      }
    },
  };
}
